/**
 * GameConnector - A utility class for inter-component communication
 *
 * This singleton class provides an event-based mechanism for components
 * to communicate with each other regardless of their position in the component tree.
 */

type EventCallback = (...args: any[]) => void;

class GameConnector {
  private static instance: GameConnector;
  private listeners: Map<string, EventCallback[]> = new Map();

  // Private constructor to enforce singleton pattern
  private constructor() {}

  // Get the singleton instance
  public static getInstance(): GameConnector {
    if (!GameConnector.instance) {
      GameConnector.instance = new GameConnector();
    }
    return GameConnector.instance;
  }

  /**
   * Register an event listener
   * @param event The event name
   * @param callback Function to call when the event is triggered
   */
  public on(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  /**
   * Remove an event listener
   * @param event The event name
   * @param callback The callback to remove
   */
  public off(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event)!;
    const index = callbacks.indexOf(callback);

    if (index !== -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Emit an event to all registered listeners
   * @param event The event name
   * @param args Arguments to pass to the listeners
   */
  public emit(event: string, ...args: any[]): void {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event)!;
    callbacks.forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in ${event} event handler:`, error);
      }
    });
  }
}

export default GameConnector;
